import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { getFormattedDateTime } from "@/utils";
import { useDebounce } from "@/hooks/use-debounce";
import { Eye, MoreHorizontal, Pencil, Plus, Search } from "lucide-react";
import { apiErrorMessage } from "@/api/perkpoint/client";
import {
  RULE_STATUS_LABELS,
  RulesApi,
  ruleKeys,
  type ApiRule,
  type RuleStatus,
} from "@/api/perkpoint/rules";
import { useActiveProgramId } from "./programs/program-scope";
import { CreateRuleForm } from "./ruleengine/CreateRuleForm";
import {
  RULE_STATUS_STYLES,
  ViewRuleModal,
  ruleActions,
  useRuleAction,
} from "./ruleengine/ViewRuleModal";

type StatusTab = "all" | RuleStatus;

function TypePills({ rule }: { rule: ApiRule }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {rule.is_earn_rule ? (
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
          Earn
        </span>
      ) : null}
      {rule.is_redemption_rule ? (
        <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-800">
          Redeem
        </span>
      ) : null}
      {rule.is_validity_rule ? (
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
          Validity
        </span>
      ) : null}
    </div>
  );
}

export default function RuleEngine() {
  const { programId } = useActiveProgramId();
  const [statusTab, setStatusTab] = useState<StatusTab>("all");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<ApiRule | null>(null);
  const [startInEdit, setStartInEdit] = useState(false);

  const query = {
    status: statusTab === "all" ? undefined : statusTab,
    search: debouncedSearch || undefined,
    pageSize: 100,
  };
  const rulesQuery = useQuery({
    queryKey: ruleKeys.list(programId ?? "", query),
    queryFn: () => RulesApi.list(programId!, query),
    enabled: Boolean(programId),
    placeholderData: keepPreviousData,
  });
  const rules = rulesQuery.data?.items ?? [];
  const stats = rulesQuery.data?.stats;
  const actionMutation = useRuleAction(programId ?? "");

  const tabs: { id: StatusTab; label: string; count?: number }[] = [
    { id: "all", label: "All rules" },
    { id: "active", label: "Live", count: stats?.active },
    { id: "pending_approval", label: "Pending approval", count: stats?.pending_approval },
    { id: "draft", label: "Drafts", count: stats?.draft },
    { id: "paused", label: "Paused", count: stats?.paused },
    { id: "archived", label: "Archived" },
  ];

  const openRule = (rule: ApiRule, edit: boolean) => {
    setSelectedRule(rule);
    setStartInEdit(edit);
  };

  return (
    <div className="mx-auto min-w-0 max-w-6xl space-y-6 pb-4">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm text-slate-500">Program</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-950">
            Rules Engine
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500">
            Set how members earn points when an event comes in, including when
            those points become available, and the policy for redeeming them.
            New rules and edits go live after approval.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setIsCreateModalOpen(true)}
          className="h-11 rounded-full bg-emerald-700 text-white hover:bg-emerald-800"
          disabled={!programId}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Add rule
        </Button>
      </header>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setStatusTab(tab.id)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                statusTab === tab.id
                  ? "border-emerald-700 bg-emerald-50 text-emerald-800"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
              )}
            >
              {tab.label}
              {tab.count ? (
                <span className="ml-1.5 rounded-full bg-white px-1.5 text-xs text-slate-600 ring-1 ring-slate-200">
                  {tab.count}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <div className="relative w-full lg:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or code"
            className="h-10 bg-white pl-9"
          />
        </div>
      </div>

      {!programId || rulesQuery.isLoading ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06]">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="pl-5">Rule</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Setup</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="w-12 pr-4" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-12 text-center text-sm text-slate-500">
                        {rulesQuery.isError
                          ? apiErrorMessage(rulesQuery.error, "Could not load rules")
                          : statusTab === "all" && !debouncedSearch
                            ? "No rules yet. Add the first one."
                            : "No rules match."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rules.map((rule) => {
                      const setup = [rule.summary.earn, rule.summary.redemption, rule.summary.validity]
                        .filter(Boolean)
                        .join(" · ");
                      const quickActions = ruleActions(rule).filter((a) => a.action !== "reject");
                      return (
                        <TableRow key={rule.id}>
                          <TableCell className="pl-5">
                            <button
                              type="button"
                              onClick={() => openRule(rule, false)}
                              className="text-left font-medium text-slate-950 hover:text-emerald-800"
                            >
                              {rule.name}
                            </button>
                            <p className="font-mono text-xs text-slate-500">{rule.code}</p>
                          </TableCell>
                          <TableCell>
                            <TypePills rule={rule} />
                          </TableCell>
                          <TableCell className="max-w-md text-sm text-slate-600">
                            {setup || "—"}
                          </TableCell>
                          <TableCell>
                            <span
                              className={cn(
                                "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                                RULE_STATUS_STYLES[rule.status],
                              )}
                            >
                              {RULE_STATUS_LABELS[rule.status]}
                            </span>
                            {rule.has_pending_changes && rule.status === "active" ? (
                              <p className="mt-1 text-xs text-amber-700">
                                {rule.awaiting_approval ? "Edit awaiting approval" : "Unsubmitted edit"}
                              </p>
                            ) : null}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm text-slate-600">
                            {getFormattedDateTime(rule.updated_at) || "—"}
                          </TableCell>
                          <TableCell className="pr-4">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  aria-label={`Actions for ${rule.name}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openRule(rule, false)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View details
                                </DropdownMenuItem>
                                {rule.status !== "archived" ? (
                                  <DropdownMenuItem onClick={() => openRule(rule, true)}>
                                    <Pencil className="mr-2 h-4 w-4" />
                                    Edit
                                  </DropdownMenuItem>
                                ) : null}
                                {quickActions.length ? <DropdownMenuSeparator /> : null}
                                {quickActions.map(({ action, label, tone }) => (
                                  <DropdownMenuItem
                                    key={action}
                                    className={cn(tone === "danger" && "text-red-600 focus:text-red-600")}
                                    disabled={actionMutation.isPending}
                                    onClick={() => actionMutation.mutate({ rule, action })}
                                  >
                                    {label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add rule</DialogTitle>
            <DialogDescription>
              Choose earn, redemption, or validity. An earn rule runs when an
              event comes in, and can hold points until after checkout. It
              starts as a draft.
            </DialogDescription>
          </DialogHeader>
          <CreateRuleForm
            onSuccess={() => setIsCreateModalOpen(false)}
            onCancel={() => setIsCreateModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {selectedRule && programId ? (
        <ViewRuleModal
          rule={selectedRule}
          programId={programId}
          isOpen
          startInEdit={startInEdit}
          onClose={() => {
            setSelectedRule(null);
            setStartInEdit(false);
          }}
        />
      ) : null}
    </div>
  );
}
